import DeactivateUser from '@Application/Users/Users/Commands/DeactivateUser';
import DeleteUser from '@Application/Users/Users/Commands/DeleteUser';
import ResetUserMfa from '@Application/Users/Users/Commands/ResetUserMfa';
import deactivateUser from '@Application/Users/Users/deactivateUser';
import deleteUser from '@Application/Users/Users/deleteUser';
import resetUserMfa from '@Application/Users/Users/resetUserMfa';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import userToDeactivateSignal, {
	setUserToDeactivate,
} from '@UI/Pages/Administration/Users/Signals/userToDeactivateSignal';
import userToDeleteSignal, {
	setUserToDelete,
} from '@UI/Pages/Administration/Users/Signals/userToDeleteSignal';
import userToResetMfaSignal, {
	setUserToResetMfa,
} from '@UI/Pages/Administration/Users/Signals/userToResetMfaSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog, ConfirmDialog } from '@verclam/ui-kit';

/**
 * The four dialogs mounted unconditionally alongside the users list: the generic imperative
 * `ConfirmDialog` host, the deactivation confirmation, the MFA reset confirmation and the invitation
 * deletion confirmation (retype the address). The host is a
 * bare `<ConfirmDialog />` — no `message`, no `visible` — which is exactly what makes it a host rather
 * than a third declarative dialog: only an instance with neither prop subscribes to `confirmDialog()`
 * calls. It is what `UserCardActions` (cancel invitation, reset password) and `UserSidebar`
 * (authentication mode change) actually open; without it their `confirmDialog()` calls are silently
 * lost, since this is the only host mounted on this page. Self-contained — reads the user list already
 * fetched by `UsersContent` through a plain `useSelector`, and the three pending-confirmation signals,
 * rather than through props: the page has nothing of its own to pass it.
 */
const UsersConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const accountId = currentUser.account?.id ?? null;
	const usersList = useSelector((state) => state.usersStore.users);

	const userToDeactivateId = useSignalValue(userToDeactivateSignal);
	const userToResetMfaId = useSignalValue(userToResetMfaSignal);
	const userToDeleteId = useSignalValue(userToDeleteSignal);

	const userToDeactivate = React.useMemo(
		() => usersList?.findById(userToDeactivateId) ?? null,
		[usersList, userToDeactivateId],
	);

	const userToResetMfa = React.useMemo(
		() => usersList?.findById(userToResetMfaId) ?? null,
		[usersList, userToResetMfaId],
	);

	const userToDelete = React.useMemo(
		() => usersList?.findById(userToDeleteId) ?? null,
		[usersList, userToDeleteId],
	);

	const handleDeleteDialogHide = React.useCallback(() => {
		setUserToDelete(null);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		if (accountId === null || userToDeleteId === null) {
			return;
		}

		dispatch(deleteUser(new DeleteUser(accountId, userToDeleteId))).catch(ignoreHandledError);
		// Close immediately: `acceptCallback` never triggers `onHide`.
		setUserToDelete(null);
	}, [accountId, userToDeleteId, dispatch]);

	const handleDeactivateDialogHide = React.useCallback(() => {
		setUserToDeactivate(null);
	}, []);

	const handleDeactivateAccept = React.useCallback(() => {
		if (accountId === null || userToDeactivateId === null) {
			return;
		}

		dispatch(deactivateUser(new DeactivateUser(accountId, userToDeactivateId))).catch(ignoreHandledError);
	}, [accountId, userToDeactivateId, dispatch]);

	const handleResetMfaDialogHide = React.useCallback(() => {
		setUserToResetMfa(null);
	}, []);

	const handleResetMfaAccept = React.useCallback(() => {
		if (accountId === null || userToResetMfaId === null) {
			return;
		}

		dispatch(resetUserMfa(new ResetUserMfa(accountId, userToResetMfaId))).catch(ignoreHandledError);
		// Close immediately: `acceptCallback` never triggers `onHide` on `ConfirmDeleteDialog`, and
		// waiting for the request to settle would leave the dialog open long enough for a second click.
		setUserToResetMfa(null);
	}, [accountId, userToResetMfaId, dispatch]);

	return (
		<React.Fragment>
			<ConfirmDialog />
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={userToDeactivateId !== null}
				onHide={handleDeactivateDialogHide}
				header="Désactiver le compte"
				message={
					<React.Fragment>
						Désactiver le compte de <strong>{userToDeactivate?.fullName ?? ''}</strong> ? Il ne
						pourra plus se connecter et ses sessions en cours seront fermées.
					</React.Fragment>
				}
				acceptLabel="Désactiver"
				acceptSeverity="danger"
				accept={handleDeactivateAccept}
			/>
			<ConfirmDeleteDialog
				visible={userToResetMfaId !== null}
				onHide={handleResetMfaDialogHide}
				header="Réinitialiser le second facteur"
				message={
					<React.Fragment>
						Cette action supprime l'appairage et les codes de récupération de{' '}
						<strong>{userToResetMfa?.fullName ?? ''}</strong>.
					</React.Fragment>
				}
				confirmText={userToResetMfa?.fullName ?? ''}
				acceptCallback={handleResetMfaAccept}
			/>
			<ConfirmDeleteDialog
				visible={userToDeleteId !== null}
				onHide={handleDeleteDialogHide}
				header="Supprimer l'invitation"
				message={
					<React.Fragment>
						Cette action est irréversible. L'invitation de{' '}
						<strong>{userToDelete?.fullName ?? ''}</strong> sera supprimée et l'adresse{' '}
						<strong>{userToDelete?.email ?? ''}</strong> pourra être réinvitée.
					</React.Fragment>
				}
				confirmText={userToDelete?.email ?? ''}
				acceptCallback={handleDeleteAccept}
			/>
		</React.Fragment>
	);
};

export default UsersConfirmationDialogs;
